import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  Button,
  Text,
  Card,
  Tab,
  Icon,
  CardColors,
  Textarea,
  Separator,
} from '@/ui'
import { DateTime } from 'luxon'
import parse from 'html-react-parser'

import {
  createUserRecordNotes,
  deleteUserRecordNote,
  fetchUserRecordNotes,
  sendSlackNotification,
  UserRecordNote,
} from '@/actions/userRecordNotesActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TextColors, TypographyStyles } from '@/styles'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
export const UserRecordNotes = ({ userId }: { userId: number }) => {
  const [notes, setNotes] = useState<Map<number, UserRecordNote>>(new Map())

  useEffect(() => {
    const fetchNotes = async () => {
      const response = await fetchUserRecordNotes(userId)

      if (response) {
        const newNotes = new Map(response.data.map((obj) => [obj.id, obj]))

        setNotes(newNotes)
      }
    }

    fetchNotes()
  }, [userId])

  return (
    <Card className="w-full">
      <Text variant={TypographyStyles.H2}>User Record Notes</Text>
      <br />
      <Tab
        panes={[
          {
            menuItem: 'Onboarding',
            render: () => (
              <NotesSection
                userId={userId}
                view="Onboarding"
                notes={notes}
                setNotes={setNotes}
              />
            ),
          },
          {
            menuItem: 'Bookkeeping',
            render: () => (
              <NotesSection
                userId={userId}
                view="Bookkeeping"
                notes={notes}
                setNotes={setNotes}
              />
            ),
          },
          {
            menuItem: 'Check-in',
            render: () => (
              <NotesSection
                userId={userId}
                view="Check-in"
                notes={notes}
                setNotes={setNotes}
              />
            ),
          },
          {
            menuItem: 'Taxes',
            render: () => (
              <NotesSection
                userId={userId}
                view="Taxes"
                notes={notes}
                setNotes={setNotes}
              />
            ),
          },
        ]}
      />
    </Card>
  )
}

interface NotesContainerProps {
  userId: number
  view: string
  notes: Map<number, UserRecordNote>
  setNotes: Dispatch<SetStateAction<Map<number, UserRecordNote>>>
}
const NotesSection = ({
  userId,
  view,
  notes,
  setNotes,
}: NotesContainerProps) => {
  const deleteNote = async (noteId: number) => {
    await deleteUserRecordNote(noteId)
    setNotes((prev) => {
      const newState = new Map(prev)
      newState.delete(noteId)
      return newState
    })
  }
  return (
    <div className="">
      {notes &&
        Array.from(notes.values())
          .filter((note: UserRecordNote) => note.category === view)
          .map((note: UserRecordNote) => (
            <Card
              key={note.id}
              color={CardColors.LEVEL_1}
              className="p-1 border border-neutral-200 bg-neutral-25 rounded-sm"
            >
              <div className="space-y-1 flex flex-row space-x-4">
                <Text color={TextColors.HEADER}>
                  {note.notesAuthor
                    ? `${note.notesAuthor.firstName} ${note.notesAuthor.lastName}`
                    : 'deleted user'}
                </Text>
                <Text color={TextColors.MUTED}>
                  {DateTime.fromISO(note.createdAt).toFormat(
                    DATE_FORMATS_LUXON.DISPLAY_SHORT
                  )}
                </Text>
              </div>
              <Separator />
              <div className="flex justify-between items-start mt-2">
                <Text variant={TypographyStyles.BODY_MD}>
                  {parse(note.text)}
                </Text>
                <Button
                  variant="ghost"
                  onClick={() => deleteNote(note.id)}
                  className="ml-4 text-red-500 hover:text-red-700"
                >
                  <Icon icon={faTrash} color={TextColors.DANGER} />
                </Button>
              </div>
            </Card>
          ))}
      <div className="mt-4">
        <DraftEditor userId={userId} view={view} setNotes={setNotes} />
      </div>
    </div>
  )
}

const DraftEditor = ({
  userId,
  view,
  setNotes,
}: {
  userId: number
  view: string
  setNotes: Dispatch<SetStateAction<Map<number, UserRecordNote>>>
}) => {
  const [value, setValue] = useState('')

  const submitNotes = useCallback(async () => {
    const createdNotes = await createUserRecordNotes(userId, value, view)

    if (!createdNotes) {
      return
    }

    setValue('')
    setNotes((prev) => new Map([...prev, [createdNotes.id, createdNotes]]))
    await sendSlackNotification(userId, value, view)
  }, [setNotes, userId, value, view])

  return (
    <div className="space-y-2">
      <Textarea
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add notes here"
        value={value}
      />
      <div className="flex justify-end">
        <Button onClick={submitNotes}>Create Note</Button>
      </div>
    </div>
  )
}
